import axios from 'axios';

/**
 * Utility to handle session refreshing and authentication
 */
class SessionRefreshUtil {
  private refreshPromise: Promise<boolean> | null = null;
  private lastRefreshTime: number = 0;
  private readonly MIN_REFRESH_INTERVAL = 60000; // 1 minute in milliseconds

  /**
   * Attempts to refresh the user's session
   * @returns Promise that resolves to true if refresh was successful
   */
  public async refreshSession(): Promise<boolean> {
    const now = Date.now();
    
    // Don't refresh too frequently
    if (now - this.lastRefreshTime < this.MIN_REFRESH_INTERVAL) {
      console.log('Session refresh skipped - too soon since last refresh');
      return true;
    }
    
    // If a refresh is already in progress, return that promise
    if (this.refreshPromise) {
      return this.refreshPromise;
    }
    
    // Start a new refresh
    this.refreshPromise = this.doRefresh();
    
    try {
      const result = await this.refreshPromise;
      this.lastRefreshTime = Date.now();
      return result;
    } finally {
      this.refreshPromise = null;
    }
  }
  
  /**
   * Performs the actual session refresh
   */
  private async doRefresh(): Promise<boolean> {
    try {
      console.log('Attempting to refresh session...');
      
      // Call your session refresh endpoint
      const response = await axios.get('/api/auth/refresh-session', {
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        }
      });
      
      console.log('Session refresh response:', response.status);
      
      if (response.status === 200) {
        console.log('Session refreshed successfully');
        return true;
      }
      
      console.warn('Session refresh failed with status:', response.status);
      return false;
    } catch (error) {
      console.error('Error refreshing session:', error);
      return false;
    }
  }
  
  /**
   * Checks if the user is currently authenticated
   */
  public async checkAuthentication(): Promise<boolean> {
    try {
      const response = await axios.get('/api/auth/check', {
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        }
      });
      
      return response.status === 200;
    } catch (error) {
      console.error('Authentication check failed:', error);
      return false;
    }
  }
}


// Export a singleton instance
export const sessionRefresh = new SessionRefreshUtil();
export default sessionRefresh; 